import { authApi, userApi } from "@/api";
import router, { ViewName } from "@/router";
import {
  readToken,
  writeToken,
  clearToken,
  readRemember,
  writeRemembered,
} from "@/utils/tokenStorage";
import {
  subscriptionOf,
  isPro,
  notificationsOf,
  localeOf,
  FREE_SERIES_LIMIT,
} from "@/utils/user";

export default {
  state: () => ({
    status: "",
    user: null,
    token: readToken(),
    remember: readRemember(),
  }),

  getters: {
    authToken: (state) => state.token,
    isLoggedIn: (state) => !!state.token,
    authUser: (state) => state.user,
    userRoles: (state) => state.user?.roles ?? [],
    rememberMe: (state) => state.remember,

    subscription: (state) => subscriptionOf(state.user),
    isProUser: (state) => isPro(state.user),
    notificationSettings: (state) => notificationsOf(state.user),
    userLocale: (state) => localeOf(state.user),

    /** Free accounts may track a single series; Pro is unlimited. */
    seriesLimit: (_state, getters) =>
      getters.isProUser ? Infinity : FREE_SERIES_LIMIT,
    canTrackMore: (_state, getters) =>
      Object.keys(getters.seriesList).length < getters.seriesLimit,
  },

  mutations: {
    authRequest(state) {
      state.status = "loading";
    },
    authSuccess(state, { token, user }) {
      state.status = "success";
      state.token = token;
      state.user = user;
    },
    authError(state) {
      state.status = "error";
    },
    updateUser(state, user) {
      state.user = user;
    },
    setRemember(state, remember) {
      state.remember = remember;
    },
    logoutAuth(state) {
      state.status = "";
      state.token = "";
      state.user = null;
      clearToken();
      // Ignore redundant-navigation errors (e.g. already on the login page).
      router.push({ name: ViewName.UserLogin }).catch(() => undefined);
    },
  },

  actions: {
    login({ commit, dispatch }, { email, password, remember = false }) {
      commit("authRequest");
      return authApi
        .authenticate({ email, password })
        .then((response) => {
          // "Remember me" decides whether the token outlives the tab.
          writeToken(response.access_token, remember);
          writeRemembered(remember, email);
          commit("setRemember", remember);
          commit("authSuccess", {
            token: response.access_token,
            user: response.user,
          });

          dispatch("getAuthenticatedUser");
          dispatch("fetchAll");
          return response;
        })
        .catch((error) => {
          commit("authError");
          clearToken();
          throw error;
        });
    },

    /**
     * Sign out. Clears the session even if the API call fails — a network
     * error must never leave someone stuck signed in.
     */
    logout({ commit }) {
      return authApi
        .logout()
        .catch(() => undefined)
        .finally(() => commit("logoutAuth"));
    },

    getAuthenticatedUser({ commit }) {
      commit("authRequest");
      return authApi.getAuthenticatedUser().then((user) => {
        commit("updateUser", user);
        return user;
      });
    },

    saveUserGenres({ commit }, genres) {
      return authApi.saveGenres(genres).then((user) => {
        commit("updateUser", user);
        return user;
      });
    },

    update({ commit }, { userId, formData }) {
      return userApi.updateUser(userId, formData).then((user) => {
        commit("updateUser", user);
        return user;
      });
    },

    /** Create an account. The API mails a verification link. */
    register(_ctx, payload) {
      return authApi.register(payload);
    },

    /**
     * Update the signed-in user's name / email.
     *
     * Resolves to { user, emailChanged } — changing the address invalidates
     * verification, so the caller has to tell the user to check their mail.
     */
    updateProfile({ commit }, { name, email }) {
      return authApi.updateProfile({ name, email }).then((response) => {
        commit("updateUser", response.user);
        return response;
      });
    },

    /** Change the signed-in user's password. */
    changePassword(_ctx, payload) {
      return authApi.changePassword(payload);
    },

    /**
     * Update language / notifications / plan.
     *
     * Applied optimistically so the UI reacts immediately, and the language is
     * mirrored to localStorage so it survives a signed-out reload.
     */
    updateSettings({ commit, state }, settings) {
      if (!state.user) return Promise.reject(new Error("No user loaded"));

      const merged = { ...state.user, ...settings };
      commit("updateUser", merged);

      if (settings.locale) {
        localStorage.setItem("rt-locale", settings.locale);
      }

      return authApi.updateSettings(settings).then((user) => {
        commit("updateUser", user);
        return user;
      });
    },

    delete(_ctx, { userId }) {
      return userApi.deleteUser(userId);
    },

    sendPasswordResetMail(_ctx, payload) {
      // Accepts a bare email for callers that have no form to fingerprint.
      const { email, ...honeypot } =
        typeof payload === "string" ? { email: payload } : payload;
      return authApi.sendPasswordReset(email, honeypot);
    },

    sendVerifyAccountEmail(_ctx, userId) {
      return authApi.sendVerify(userId);
    },

    setPassword(_ctx, { userId, formData }) {
      return authApi.setPassword(userId, formData);
    },

    verify(_ctx, { hash, userId, token }) {
      return authApi.verify(hash, userId, token);
    },

    verifyAccount(_ctx, { hash, userId, token }) {
      return authApi.verifyAccount(hash, userId, token);
    },
  },
};
